import { ArrowRight, MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const fallbackJobs = [{
  id: "fb-1",
  title: "Lead Financial Analyst",
  company: "Nexus Talent Partners",
  badge: "HOT",
  stack: ["FINANCE", "STRATEGY", "EXCEL", "REPORTING"],
  location: "New York, NY",
  salary: "$160K - $210K",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701280/img/nexus_finance_role.png"
}, {
  id: "fb-2",
  title: "Senior Tech Recruiter",
  company: "Nexus Talent Partners",
  badge: "NEW",
  stack: ["SOURCING", "TALENT", "STRATEGY", "NETWORKING"],
  location: "Remote / Global",
  salary: "$120K - $180K",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701280/img/nexus_recruitment_role.png"
}, {
  id: "fb-3",
  title: "Healthcare Operations Manager",
  company: "Nexus Talent Partners",
  badge: "HOT",
  stack: ["HEALTHCARE", "OPERATIONS", "COMPLIANCE", "LEADERSHIP"],
  location: "Chicago, IL",
  salary: "$140K - $190K",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701280/img/nexus_healthcare_role.png"
}];
function extractStack(content: string | undefined): string[] {
  if (!content) return ["STRATEGY", "LEADERSHIP", "GROWTH"];
  const candidates = ["FINANCE", "TECH", "HEALTHCARE", "STRATEGY", "LEADERSHIP", "OPERATIONS", "COMPLIANCE", "SOURCING", "TALENT", "GROWTH", "MANAGEMENT", "ANALYSIS"];
  const upper = content.toUpperCase();
  const matched = candidates.filter(c => upper.includes(c)).slice(0, 4);
  return matched.length > 0 ? matched : ["STRATEGY", "LEADERSHIP", "GROWTH"];
}
function LatestOpportunitiesConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-72 rounded-lg border border-border bg-card animate-pulse" />)}
      </div>;
  }
  const showFallback = error || posts.length === 0;
  const items = showFallback ? fallbackJobs : posts.slice(0, 3).map((post, idx) => ({
    id: post.id,
    title: post.title,
    company: post.author?.name || "Nexus Talent Partners",
    badge: idx === 0 ? "HOT" : "NEW",
    stack: extractStack(post.excerpt || post.content),
    location: "Global",
    salary: "Competitive",
    logo: post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701280/img/nexus_default_role.png",
    link: post.link
  }));
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {items.map((job, i) => {
      const href = (job as any).link || "#";
      return <div key={job.id} data-index={i} className="group relative overflow-hidden rounded-lg border border-border bg-card p-8 transition-all duration-300 hover:border-primary">
            <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" style={{
          background: "linear-gradient(135deg, hsl(38 48% 56% / 0.08) 0%, transparent 50%, hsl(38 48% 56% / 0.04) 100%)"
        }} />

            <div className="relative flex items-start justify-between">
              <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
                <img src={job.logo} alt={`${job.company} logo`} className="h-full w-full object-cover" loading="lazy" />
              </div>
              <Badge variant="outline" className="border-primary bg-primary/10 font-mono text-[10px] uppercase tracking-wider text-primary">
                {job.badge}
              </Badge>
            </div>

            <div className="relative mt-6">
              <h3 className="font-default text-2xl font-semibold text-foreground transition-transform duration-300 group-hover:translate-x-1">
                {job.title}
              </h3>
              <p className="mt-1 text-[15px] text-muted-foreground">
                {job.company}
              </p>
            </div>

            <div className="relative mt-5 flex flex-wrap gap-2">
              {job.stack.map((tech, idx) => <span key={idx} data-index={idx} className="rounded-lg border border-primary/20 bg-primary/5 px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-primary">
                  {tech}
                </span>)}
            </div>

            <div className="relative mt-8 flex items-center justify-between border-t border-border pt-5">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" />
                <span>{job.location}</span>
              </div>
              <span className="font-mono text-xs text-foreground/80">
                {job.salary}
              </span>
            </div>

            <Link to={href} className="relative mt-5 flex items-center gap-1.5 font-mono text-sm font-medium text-primary transition-all hover:gap-2.5">
              View Details
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>;
    })}
    </div>;
}
export default function OpenPositions() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="736" id="open-positions" className="bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 lg:py-[120px]">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-lg bg-primary opacity-75"></span>
                <span className="relative inline-flex h-2 w-2 rounded-lg bg-primary"></span>
              </span>
              <span className="font-mono text-xs uppercase tracking-widest text-primary">
                Current Openings
              </span>
            </div>
            <h2 className="font-default mt-5 text-4xl font-semibold tracking-tight text-foreground sm:text-5xl lg:text-[56px] lg:leading-[1.05]">
              Career Opportunities.
            </h2>
          </div>

          <Link to="/JobBoardPage">
            <Button variant="outline" size="lg" className="border-primary bg-transparent font-mono text-sm uppercase tracking-wider text-foreground hover:bg-primary hover:text-white">
              View All Roles
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>

        <PostsProvider className="mt-16" wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date"
      }}>
          <LatestOpportunitiesConsumer />
        </PostsProvider>
      </div>
    </section>;
}